import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

const DailyTaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [newTaskName, setNewTaskName] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mealCount, setMealCount] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [user, setUser] = useState(null);
  const [taskFrequency, setTaskFrequency] = useState("today"); // New state for task frequency
  const [customDays, setCustomDays] = useState([]); // For custom days
  const [showTaskForm, setShowTaskForm] = useState(false); // To toggle the task creation form

  const formatDate = (date) => date.toISOString().split("T")[0];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      fetchDailyTasks(selectedDate, user);
    }
  }, [selectedDate, user]);

  const fetchDailyTasks = async (date, user) => {
    const formattedDate = formatDate(date);
    const dailyTaskRef = doc(collection(db, "dailyTasks"), formattedDate);

    const dailyTaskSnapshot = await getDoc(dailyTaskRef);
    if (dailyTaskSnapshot.exists()) {
      setTasks(dailyTaskSnapshot.data().subtasks || []);
    } else {
      setTasks([]); // Start with no tasks for the user
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim()) return;

    const newTask = {
      id: Date.now().toString(),
      name: newTaskName,
      completed: false,
      frequency: taskFrequency, // Store the frequency
      customDays: taskFrequency === "custom" ? customDays : [], // Store custom days if applicable
    };

    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);

    try {
      const formattedDate = formatDate(selectedDate);
      const dailyTaskRef = doc(collection(db, "dailyTasks"), formattedDate);
      await updateDoc(dailyTaskRef, { subtasks: updatedTasks });
    } catch (error) {
      console.error("Error adding task:", error);
    }

    setNewTaskName("");
    setShowTaskForm(false); // Close the form after submission
  };

  const toggleTaskCompletion = async (taskId) => {
    const updatedTasks = tasks.map((task) =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    setTasks(updatedTasks);

    try {
      const formattedDate = formatDate(selectedDate);
      const dailyTaskRef = doc(collection(db, "dailyTasks"), formattedDate);
      await updateDoc(dailyTaskRef, { subtasks: updatedTasks });
    } catch (error) {
      console.error("Error updating task:", error);
    }
  };

  const handleMealCountChange = async (count) => {
    setMealCount(count);

    const updatedTasks = tasks.map((task) =>
      task.id === "meals" ? { ...task, completed: count >= 3 } : task
    );
    setTasks(updatedTasks);

    try {
      const formattedDate = formatDate(selectedDate);
      const dailyTaskRef = doc(collection(db, "dailyTasks"), formattedDate);
      await updateDoc(dailyTaskRef, { subtasks: updatedTasks });
    } catch (error) {
      console.error("Error updating meals task:", error);
    }
  };

  const handleDateChange = (direction) => {
    setIsTransitioning(true);
    setTimeout(() => {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + direction);
      setSelectedDate(newDate);
      setIsTransitioning(false);
    }, 300);
  };

  const handleFrequencyChange = (e) => {
    setTaskFrequency(e.target.value);
    if (e.target.value !== "custom") setCustomDays([]); // Clear custom days if not custom frequency
  };

  const handleCustomDaysChange = (e) => {
    const { value, checked } = e.target;
    setCustomDays((prevDays) =>
      checked ? [...prevDays, value] : prevDays.filter((day) => day !== value)
    );
  };

  return (
    <Container>
      <h2>Habit Tracker</h2>

      <TaskList isTransitioning={isTransitioning}>
        {tasks.map((task) => (
          <Task key={task.id} completed={task.completed}>
            <TaskName completed={task.completed}>{task.name}</TaskName>
            {task.id === "meals" ? (
              <MealInput
                type="number"
                min="0"
                value={mealCount}
                onChange={(e) => handleMealCountChange(Number(e.target.value))}
              />
            ) : (
              <Button
                completed={task.completed}
                onClick={() => toggleTaskCompletion(task.id)}
              >
                {task.completed ? "Undo" : "Complete"}
              </Button>
            )}
          </Task>
        ))}
      </TaskList>

      {/* Show the add task form */}
      {showTaskForm ? (
        <AddTaskForm onSubmit={addTask}>
          <TaskInput
            type="text"
            placeholder="Add a new task..."
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
          />
          <SelectFrequency>
            <label>
              Frequency:
              <select value={taskFrequency} onChange={handleFrequencyChange}>
                <option value="today">Only Today</option>
                <option value="everyday">Everyday</option>
                <option value="custom">Custom Days</option>
              </select>
            </label>

            {taskFrequency === "custom" && (
              <CustomDays>
                <label>
                  <input
                    type="checkbox"
                    value="Monday"
                    onChange={handleCustomDaysChange}
                  />{" "}
                  Monday
                </label>
                <label>
                  <input
                    type="checkbox"
                    value="Tuesday"
                    onChange={handleCustomDaysChange}
                  />{" "}
                  Tuesday
                </label>
                <label>
                  <input
                    type="checkbox"
                    value="Wednesday"
                    onChange={handleCustomDaysChange}
                  />{" "}
                  Wednesday
                </label>
                <label>
                  <input
                    type="checkbox"
                    value="Thursday"
                    onChange={handleCustomDaysChange}
                  />{" "}
                  Thursday
                </label>
                <label>
                  <input
                    type="checkbox"
                    value="Friday"
                    onChange={handleCustomDaysChange}
                  />{" "}
                  Friday
                </label>
                <label>
                  <input
                    type="checkbox"
                    value="Saturday"
                    onChange={handleCustomDaysChange}
                  />{" "}
                  Saturday
                </label>
                <label>
                  <input
                    type="checkbox"
                    value="Sunday"
                    onChange={handleCustomDaysChange}
                  />{" "}
                  Sunday
                </label>
              </CustomDays>
            )}
          </SelectFrequency>

          <AddButton type="submit">Add Task</AddButton>
          <CancelButton onClick={() => setShowTaskForm(false)}>
            Cancel
          </CancelButton>
        </AddTaskForm>
      ) : (
        <AddButton onClick={() => setShowTaskForm(true)}>+ Add Task</AddButton>
      )}

      <DateSelector>
        <ArrowButton onClick={() => handleDateChange(-1)}>&lt;</ArrowButton>
        <DateDisplay>{formatDate(selectedDate)}</DateDisplay>
        <ArrowButton onClick={() => handleDateChange(1)}>&gt;</ArrowButton>
      </DateSelector>
    </Container>
  );
};

const SelectFrequency = styled.div`
  margin-top: 10px;
`;

const CustomDays = styled.div`
  margin-top: 10px;
`;

const CancelButton = styled.button`
  padding: 5px 10px;
  font-size: 14px;
  background: #ff4d4f;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: "Comfortaa";
`;
const MealInput = styled.input`
  width: 50px;
  padding: 5px;
  font-size: 14px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 5px;
`;
// Keyframes for animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(10px); }
`;

// Styled Components
const Container = styled.div`
  width: 35%;
  padding: 40px;
  background-color: #171c29;
  border-radius: 20px;
  h2 {
    font-weight: 300;
    text-align: center;
    margin-bottom: 20px;
  }
  @media (max-width: 500px) {
    width: 100%;
    min-width: 100px;
  }
  @media (max-width: 1420px) {
    width: 60%;
    /* min-width: 100px; */
  }
  @media (max-width: 700px) {
    padding: 20px;
  }
  @media (max-width: 680px) {
    width: 100%;
    min-width: 100px;
  }
`;

const DateSelector = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const DateDisplay = styled.div`
  margin: 0 20px;
  font-size: 18px;
  color: white;
`;

const TaskList = styled.div`
  animation: ${(props) => (props.isTransitioning ? fadeOut : fadeIn)} 0.3s ease;
`;

const Task = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 10px 15px;
  background: ${(props) => (props.completed ? "#20273a" : "#20273a")};
  border-radius: 15px;
  color: white;
  animation: ${fadeIn} 0.3s ease;
`;

const TaskName = styled.span`
  font-size: 16px;
  color: ${(props) => (props.completed ? "#5a5a5a" : "#ffffff")};
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
`;

const Button = styled.button`
  padding: 8px 10px;
  font-size: 14px;
  background: ${(props) => (props.completed ? "#d9544f37" : "#ffffff")};
  color: ${(props) => (props.completed ? "#ffffff" : "#303030")};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: "Comfortaa";
  &:hover {
    opacity: 0.9;
  }
`;

const AddTaskForm = styled.form`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const TaskInput = styled.input`
  width: 100%;
  padding: 15px;
  font-size: 14px;
  border-radius: 15px 0 0 15px;
  border: none;
  background-color: #3b445c;
  color: white;
  outline: none;
  margin-right: 10px;
  font-family: "Comfortaa";
`;

const AddButton = styled.button`
  padding: 10px 15px;
  font-size: 14px;
  background: #e7e7e7;
  color: #252525;
  border: none;
  border-radius: 0 15px 15px 0;
  cursor: pointer;
  font-family: "Comfortaa";
  transition: 0.4s;
  &:hover {
    background: #18812a;
    color: white;
  }
`;

export default DailyTaskManager;
